<script setup lang="ts">
import { ChevronRight, LoaderCircle } from 'lucide-vue-next'
import { computed, shallowRef, watch } from 'vue'
import type { SearchCartoonItem, SearchTopicItem, SearchUserItem } from '@/api/search'
import type { UserCartoonItem, UserWorkItem } from '@/api/home/type'
import AppErrorState from '@/components/app-error-state'
import ProfileCartoonCard from '@/components/profile-cartoon-card'
import SearchHorizontalStrip from '@/components/search-horizontal-strip'
import SummaryContentCard from '@/components/summary-content-card'
import { useElementVisibility } from '@/composables/useElementVisibility'
import { useScreenSize } from '@/composables/useScreenSize'
import type { SearchPriorityUserResult, SearchResultTab } from '@/composables/useSearchPage'
import { stateImages } from '@/utils/state-images'
import { buildImageResizeUrl } from '@/utils/distribute/media-url'
import { formatNumber } from '@/utils/number'
import {
  distributeItemsByIndex,
  distributeSummaryContentItems,
  resolveSummaryContentColumnCount,
} from '@/utils/profile/content-grid'
import type { Emits, Props } from './type'

defineOptions({
  name: 'SearchResultsPanel',
})

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  loadingPriorityUsers: false,
  priorityUsersError: null,
  hasAnyResults: false,
  canLoadMore: false,
})

const emit = defineEmits<Emits>()
const { screenWidth } = useScreenSize()
const loadMoreTarget = shallowRef<HTMLElement | null>(null)
const { isVisible: isLoadMoreVisible } = useElementVisibility(loadMoreTarget, {
  rootMargin: '420px 0px',
  threshold: 0,
})

const showInitialLoading = computed(() => props.loading)
const columnCount = computed(() => resolveSummaryContentColumnCount(screenWidth.value))
const workColumns = computed(() =>
  distributeSummaryContentItems(props.workResults.items, columnCount.value),
)
const topicPreviewItems = computed(() => props.topicResults.items.slice(0, 8))
const entityStripSkeletonItems = computed(() =>
  Array.from({ length: screenWidth.value <= 640 ? 6 : 8 }, (_, index) => index),
)
const userGridSkeletonItems = computed(() =>
  Array.from({ length: screenWidth.value <= 640 ? 10 : 12 }, (_, index) => index),
)
const topicSkeletonItems = computed(() =>
  Array.from(
    { length: props.activeTab === 'all' ? (screenWidth.value <= 640 ? 4 : 8) : 12 },
    (_, index) => index,
  ),
)
const cartoonSkeletonItems = computed(() =>
  Array.from({ length: screenWidth.value <= 640 ? 8 : 12 }, (_, index) => index),
)
const workSkeletonItems = computed(() =>
  Array.from({ length: Math.max(8, columnCount.value * 3) }, (_, index) => index),
)
const workSkeletonColumns = computed(() =>
  distributeItemsByIndex(workSkeletonItems.value, columnCount.value),
)
const combinedUserItems = computed<(SearchUserItem | SearchPriorityUserResult)[]>(() => {
  const seenIds = new Set<string>()

  return [...props.priorityUsers, ...props.userResults.items].filter((item) => {
    if (seenIds.has(item.id)) {
      return false
    }

    seenIds.add(item.id)
    return true
  })
})
const activeResultCount = computed(() => {
  if (props.activeTab === 'all') {
    return props.hasAnyResults ? Math.max(props.resultCounts.all ?? 0, 1) : 0
  }

  return props.resultCounts[props.activeTab] ?? 0
})
const emptyStateMessage = computed(() => {
  const subject = props.activeTab === 'all' ? '内容' : getTabLabel(props.activeTab)

  return `没有找到与“${props.query}”相关的${subject}`
})
const activeTabError = computed(() => {
  if (props.activeTab === 'all') {
    return (
      props.cartoonResults.error ||
      props.workResults.error ||
      props.priorityUsersError ||
      props.userResults.error ||
      props.topicResults.error
    )
  }

  if (props.activeTab === 'cartoon') {
    return props.cartoonResults.error
  }

  if (props.activeTab === 'work') {
    return props.workResults.error
  }

  if (props.activeTab === 'user') {
    return props.priorityUsersError || props.userResults.error
  }

  if (props.activeTab === 'topic') {
    return props.topicResults.error
  }

  return null
})
const showErrorState = computed(
  () => !props.loading && activeResultCount.value === 0 && Boolean(activeTabError.value),
)
const showEmptyState = computed(
  () => !props.loading && !showErrorState.value && activeResultCount.value === 0,
)
const isLoadingMore = computed(() => {
  if (props.activeTab === 'all' || props.activeTab === 'work') {
    return props.workResults.loadingMore
  }

  if (props.activeTab === 'cartoon') {
    return props.cartoonResults.loadingMore
  }

  if (props.activeTab === 'user') {
    return props.userResults.loadingMore
  }

  if (props.activeTab === 'topic') {
    return props.topicResults.loadingMore
  }

  return false
})
const showLoadMoreSentinel = computed(
  () => activeResultCount.value > 0 && (props.canLoadMore || isLoadingMore.value),
)

watch(
  [isLoadMoreVisible, () => props.canLoadMore, () => props.activeTab],
  ([visible, canLoadMore]) => {
    if (visible && canLoadMore) {
      emit('loadMore')
    }
  },
)

function getTabLabel(tab: SearchResultTab) {
  return props.tabs.find((item) => item.key === tab)?.label ?? tab
}

function getCartoonMetrics(item: SearchCartoonItem) {
  return [
    item.useNum ? `${formatNumber(item.useNum)}互动` : '',
    item.fansNum ? `${formatNumber(item.fansNum)}粉丝` : '',
    item.worksNum ? `${formatNumber(item.worksNum)}作品` : '',
  ].filter(Boolean)
}

function getUserTitle(item: SearchUserItem | SearchPriorityUserResult) {
  return item.nickname?.trim() || item.uid || '未命名用户'
}

function getUserMeta(item: SearchUserItem | SearchPriorityUserResult) {
  return item.uid || 'Dreama 用户'
}

function getUserMetrics(item: SearchUserItem | SearchPriorityUserResult) {
  return [
    item.cartoonCount ? `${formatNumber(item.cartoonCount)}角色` : '',
    item.pipeCount ? `${formatNumber(item.pipeCount)}作品` : '',
    item.worksNum ? `${formatNumber(item.worksNum)}动态` : '',
  ].filter(Boolean)
}

function getTopicMetrics(item: SearchTopicItem) {
  return [
    item.totalUseNum ? `${formatNumber(item.totalUseNum)}互动` : '',
    item.totalWorks ? `${formatNumber(item.totalWorks)}作品` : '',
  ].filter(Boolean)
}

function getCartoonDescription(item: SearchCartoonItem) {
  return item.summary?.trim() || item.features?.trim() || item.user?.nickname || ''
}

function resolveAvatar(src: string | null | undefined) {
  return buildImageResizeUrl(src, { width: 300, height: 300, mode: 'fill' }) ?? src ?? ''
}

function resolveTopicCover(src: string | null | undefined) {
  return buildImageResizeUrl(src, { width: 160, height: 160, mode: 'fill' }) ?? src ?? ''
}

function toProfileCartoonItem(item: SearchCartoonItem): UserCartoonItem {
  return {
    id: item.id,
    userId: item.userId ?? item.user?.id ?? '',
    self: item.self ?? null,
    name: item.name,
    nickname: item.user?.nickname ?? null,
    summary: getCartoonDescription(item) || null,
    features: item.features ?? null,
    avatar: item.avatar,
    banner: item.banner ?? item.bgImg ?? item.avatar,
    gender: item.gender ?? null,
    age: null,
    ynSubscribe: item.ynSubscribe ?? null,
    timbreId: null,
    timbreVal: null,
    useNum: item.useNum ?? 0,
    tplNum: item.tplNum ?? 0,
    fansNum: item.fansNum ?? 0,
    hasFollow: item.hasFollow ?? 0,
    status: item.status ?? 11,
    rel: null,
    type: item.type ?? null,
    user: null,
  }
}
</script>

<template>
  <section class="search-results" aria-label="搜索结果">
    <div class="search-results__tabs" role="tablist" aria-label="搜索结果分类">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="search-results__tab"
        :class="{ 'is-active': activeTab === tab.key }"
        role="tab"
        :aria-selected="activeTab === tab.key"
        @click="emit('update:activeTab', tab.key)"
      >
        <span>{{ getTabLabel(tab.key) }}</span>
      </button>
    </div>

    <Transition name="search-results-body" mode="out-in" appear>
      <div
        v-if="showInitialLoading"
        :key="`loading-${activeTab}`"
        class="search-results__loading-shell"
        role="status"
        aria-label="搜索结果加载中"
        aria-live="polite"
      >
        <template v-if="activeTab === 'all'">
          <section class="search-results__skeleton-section" aria-hidden="true">
            <span class="search-results__skeleton-heading" />
            <div class="search-results__skeleton-strip">
              <div
                v-for="item in entityStripSkeletonItems"
                :key="`cartoon-strip-skeleton-${item}`"
                class="search-results__entity-skeleton-card"
              >
                <span class="search-results__avatar-skeleton" />
                <span class="search-results__line-skeleton" />
                <span class="search-results__line-skeleton is-short" />
              </div>
            </div>
          </section>

          <section class="search-results__skeleton-section" aria-hidden="true">
            <span class="search-results__skeleton-heading" />
            <div class="search-results__skeleton-strip">
              <div
                v-for="item in entityStripSkeletonItems"
                :key="`user-strip-skeleton-${item}`"
                class="search-results__entity-skeleton-card"
              >
                <span class="search-results__avatar-skeleton" />
                <span class="search-results__line-skeleton" />
                <span class="search-results__line-skeleton is-short" />
              </div>
            </div>
          </section>

          <section class="search-results__skeleton-section" aria-hidden="true">
            <span class="search-results__skeleton-heading" />
            <div class="search-results__topic-grid is-preview">
              <div
                v-for="item in topicSkeletonItems"
                :key="`topic-preview-skeleton-${item}`"
                class="search-results__topic-card-skeleton"
              >
                <span class="search-results__topic-cover-skeleton" />
                <span class="search-results__topic-copy-skeleton">
                  <span class="search-results__topic-line-skeleton" />
                  <span class="search-results__topic-line-skeleton is-short" />
                </span>
              </div>
            </div>
          </section>

          <section class="search-results__skeleton-section" aria-hidden="true">
            <span class="search-results__skeleton-heading" />
            <div
              class="search-results__work-masonry"
              :style="{ '--search-work-columns': String(columnCount) }"
            >
              <div
                v-for="(column, columnIndex) in workSkeletonColumns"
                :key="`all-work-skeleton-column-${columnIndex}`"
                class="search-results__work-column"
              >
                <div
                  v-for="item in column"
                  :key="`all-work-skeleton-${item}`"
                  class="search-results__work-card-skeleton"
                >
                  <span
                    class="search-results__work-cover-skeleton"
                    :class="{ 'is-short': item % 3 === 1, 'is-square': item % 5 === 0 }"
                  />
                  <span class="search-results__work-line-skeleton" />
                  <span class="search-results__work-line-skeleton is-short" />
                </div>
              </div>
            </div>
          </section>
        </template>

        <div
          v-else-if="activeTab === 'cartoon'"
          class="search-results__cartoon-grid"
          :style="{ '--search-work-columns': String(columnCount) }"
          aria-hidden="true"
        >
          <div
            v-for="item in cartoonSkeletonItems"
            :key="`cartoon-skeleton-${item}`"
            class="search-results__cartoon-skeleton"
          >
            <div class="search-results__cartoon-cover-skeleton">
              <span class="search-results__cartoon-footer-skeleton">
                <span class="search-results__cartoon-avatar-row-skeleton">
                  <span class="search-results__cartoon-avatar-skeleton" />
                  <span class="search-results__cartoon-line-skeleton" />
                </span>
                <span class="search-results__cartoon-line-skeleton is-wide" />
                <span class="search-results__cartoon-line-skeleton is-short" />
              </span>
            </div>
          </div>
        </div>

        <div
          v-else-if="activeTab === 'work'"
          class="search-results__work-masonry"
          :style="{ '--search-work-columns': String(columnCount) }"
          aria-hidden="true"
        >
          <div
            v-for="(column, columnIndex) in workSkeletonColumns"
            :key="`work-skeleton-column-${columnIndex}`"
            class="search-results__work-column"
          >
            <div
              v-for="item in column"
              :key="`work-skeleton-${item}`"
              class="search-results__work-card-skeleton"
            >
              <span
                class="search-results__work-cover-skeleton"
                :class="{ 'is-short': item % 3 === 1, 'is-square': item % 5 === 0 }"
              />
              <span class="search-results__work-line-skeleton" />
              <span class="search-results__work-line-skeleton is-short" />
            </div>
          </div>
        </div>

        <div
          v-else-if="activeTab === 'user'"
          class="search-results__person-grid"
          aria-hidden="true"
        >
          <div
            v-for="item in userGridSkeletonItems"
            :key="`user-grid-skeleton-${item}`"
            class="search-results__entity-skeleton-card"
          >
            <span class="search-results__avatar-skeleton" />
            <span class="search-results__line-skeleton" />
            <span class="search-results__line-skeleton is-short" />
          </div>
        </div>

        <div v-else class="search-results__topic-grid" aria-hidden="true">
          <div
            v-for="item in topicSkeletonItems"
            :key="`topic-skeleton-${item}`"
            class="search-results__topic-card-skeleton"
          >
            <span class="search-results__topic-cover-skeleton" />
            <span class="search-results__topic-copy-skeleton">
              <span class="search-results__topic-line-skeleton" />
              <span class="search-results__topic-line-skeleton is-short" />
            </span>
          </div>
        </div>
      </div>

      <AppErrorState
        v-else-if="showErrorState"
        key="error"
        :message="activeTabError || undefined"
        variant="inline"
        @retry="emit('retry')"
      />

      <div
        v-else-if="showEmptyState"
        :key="`empty-${activeTab}`"
        class="search-results__empty-state"
        role="status"
        aria-live="polite"
      >
        <img
          class="search-results__empty-image"
          :src="stateImages.empty"
          alt="搜索空状态插画"
          width="160"
          height="160"
          draggable="false"
        />
        <p>{{ emptyStateMessage }}</p>
      </div>

      <div v-else :key="`content-${activeTab}-${query}`" class="search-results__body">
        <template v-if="activeTab === 'all'">
          <SearchHorizontalStrip
            v-if="cartoonResults.items.length"
            title="角色"
            :item-count="cartoonResults.items.length"
            @more="emit('update:activeTab', 'cartoon')"
          >
            <button
              v-for="item in cartoonResults.items"
              :key="item.id"
              type="button"
              class="search-results__entity-card"
              @click="emit('cartoonClick', item)"
            >
              <span class="search-results__entity-avatar">
                <img
                  :src="resolveAvatar(item.avatar)"
                  :alt="item.name"
                  loading="lazy"
                  decoding="async"
                />
              </span>
              <span class="search-results__entity-title">{{ item.name }}</span>
              <span class="search-results__entity-metrics">{{
                getCartoonMetrics(item).slice(0, 2).join(' · ')
              }}</span>
            </button>
          </SearchHorizontalStrip>

          <SearchHorizontalStrip
            v-if="combinedUserItems.length"
            title="用户"
            :item-count="combinedUserItems.length"
            @more="emit('update:activeTab', 'user')"
          >
            <button
              v-for="item in combinedUserItems"
              :key="item.id"
              type="button"
              class="search-results__entity-card"
              @click="emit('userClick', item)"
            >
              <span class="search-results__entity-avatar">
                <img
                  :src="resolveAvatar(item.avatar)"
                  :alt="getUserTitle(item)"
                  loading="lazy"
                  decoding="async"
                />
              </span>
              <span class="search-results__entity-title">{{ getUserTitle(item) }}</span>
              <span class="search-results__entity-meta">{{ getUserMeta(item) }}</span>
              <span class="search-results__entity-metrics">{{
                getUserMetrics(item).slice(0, 2).join(' · ')
              }}</span>
            </button>
          </SearchHorizontalStrip>

          <section v-if="topicPreviewItems.length" class="search-results__section">
            <div class="search-results__section-head">
              <button
                type="button"
                class="search-results__section-title-button"
                @click="emit('update:activeTab', 'topic')"
              >
                <span>话题</span>
                <ChevronRight :size="16" aria-hidden="true" />
              </button>
            </div>
            <div class="search-results__topic-grid is-preview">
              <button
                v-for="item in topicPreviewItems"
                :key="item.id"
                type="button"
                class="search-results__topic-card"
                @click="emit('topicClick', item)"
              >
                <span class="search-results__topic-cover">
                  <img
                    v-if="resolveTopicCover(item.cover)"
                    :src="resolveTopicCover(item.cover)"
                    :alt="item.name"
                    loading="lazy"
                    decoding="async"
                  />
                </span>
                <span class="search-results__topic-copy">
                  <span class="search-results__topic-title">{{ item.name }}</span>
                  <span class="search-results__topic-metrics">{{
                    getTopicMetrics(item).join(' · ')
                  }}</span>
                </span>
              </button>
            </div>
          </section>

          <section v-if="workResults.items.length" class="search-results__section">
            <div class="search-results__section-head">
              <button
                type="button"
                class="search-results__section-title-button"
                @click="emit('update:activeTab', 'work')"
              >
                <span>作品</span>
                <ChevronRight :size="16" aria-hidden="true" />
              </button>
            </div>
            <div
              class="search-results__work-masonry"
              :style="{ '--search-work-columns': String(columnCount) }"
            >
              <div
                v-for="(column, columnIndex) in workColumns"
                :key="`all-work-column-${columnIndex}`"
                class="search-results__work-column"
              >
                <SummaryContentCard
                  v-for="(item, itemIndex) in column"
                  :key="item.id"
                  :item="item"
                  :priority="columnIndex < 2 && itemIndex < 2"
                  @click="emit('workClick', item)"
                  @author-click="emit('authorClick', item)"
                />
              </div>
            </div>
          </section>
        </template>

        <template v-else-if="activeTab === 'cartoon'">
          <div
            v-if="cartoonResults.items.length"
            class="search-results__cartoon-grid"
            :style="{ '--search-work-columns': String(columnCount) }"
          >
            <ProfileCartoonCard
              v-for="item in cartoonResults.items"
              :key="item.id"
              :item="toProfileCartoonItem(item)"
              @click="emit('cartoonClick', item)"
            />
          </div>
          <AppErrorState
            v-if="cartoonResults.error"
            :message="cartoonResults.error"
            variant="inline"
            :retryable="false"
          />
        </template>

        <template v-else-if="activeTab === 'work'">
          <div
            v-if="workResults.items.length"
            class="search-results__work-masonry"
            :style="{ '--search-work-columns': String(columnCount) }"
          >
            <div
              v-for="(column, columnIndex) in workColumns"
              :key="`work-column-${columnIndex}`"
              class="search-results__work-column"
            >
              <SummaryContentCard
                v-for="(item, itemIndex) in column"
                :key="item.id"
                :item="item"
                :priority="columnIndex < 2 && itemIndex < 2"
                @click="emit('workClick', item)"
                @author-click="emit('authorClick', item)"
              />
            </div>
          </div>
          <AppErrorState
            v-if="workResults.error"
            :message="workResults.error"
            variant="inline"
            :retryable="false"
          />
        </template>

        <template v-else-if="activeTab === 'user'">
          <div v-if="combinedUserItems.length" class="search-results__person-grid">
            <button
              v-for="item in combinedUserItems"
              :key="item.id"
              type="button"
              class="search-results__entity-card"
              @click="emit('userClick', item)"
            >
              <span class="search-results__entity-avatar">
                <img
                  :src="resolveAvatar(item.avatar)"
                  :alt="getUserTitle(item)"
                  loading="lazy"
                  decoding="async"
                />
              </span>
              <span class="search-results__entity-title">{{ getUserTitle(item) }}</span>
              <span class="search-results__entity-meta">{{ getUserMeta(item) }}</span>
              <span class="search-results__entity-metrics">{{
                getUserMetrics(item).join(' · ')
              }}</span>
            </button>
          </div>

          <AppErrorState
            v-if="priorityUsersError || userResults.error"
            :message="priorityUsersError || userResults.error || undefined"
            variant="inline"
            :retryable="false"
          />
        </template>

        <template v-else-if="activeTab === 'topic'">
          <div v-if="topicResults.items.length" class="search-results__topic-grid">
            <button
              v-for="item in topicResults.items"
              :key="item.id"
              type="button"
              class="search-results__topic-card"
              @click="emit('topicClick', item)"
            >
              <span class="search-results__topic-cover">
                <img
                  v-if="resolveTopicCover(item.cover)"
                  :src="resolveTopicCover(item.cover)"
                  :alt="item.name"
                  loading="lazy"
                  decoding="async"
                />
              </span>
              <span class="search-results__topic-copy">
                <span class="search-results__topic-title">{{ item.name }}</span>
                <span class="search-results__topic-metrics">{{
                  getTopicMetrics(item).join(' · ')
                }}</span>
              </span>
            </button>
          </div>
          <AppErrorState
            v-if="topicResults.error"
            :message="topicResults.error"
            variant="inline"
            :retryable="false"
          />
        </template>

        <div
          v-if="showLoadMoreSentinel"
          ref="loadMoreTarget"
          class="search-results__load-more"
          aria-hidden="true"
        >
          <span v-if="isLoadingMore">
            <LoaderCircle :size="16" class="is-spin" />
            正在加载更多
          </span>
        </div>
      </div>
    </Transition>
  </section>
</template>

<style scoped>
.search-results {
  display: flex;
  width: 100%;
  max-width: 1440px;
  min-width: 0;
  flex-direction: column;
  gap: 16px;
  margin: 0 auto;
}

.search-results__tabs {
  position: sticky;
  top: 62px;
  z-index: 3;
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: center;
  overflow-x: auto;
  padding: 0 12px;
  background: var(--app-bg);
  box-shadow: 0 1px 0 var(--app-border-subtle);
  scrollbar-width: none;
}

.search-results__tabs::-webkit-scrollbar {
  display: none;
}

.search-results__tab {
  position: relative;
  display: inline-flex;
  min-width: max-content;
  align-items: center;
  justify-content: center;
  padding: 8px 12px 12px;
  border: 0;
  appearance: none;
  background: transparent;
  color: var(--app-text-muted);
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  outline: none;
  transition: color var(--app-interaction-duration) var(--app-interaction-ease);
}

.search-results__tab::after {
  position: absolute;
  right: 12px;
  bottom: 4px;
  left: 12px;
  height: 1px;
  background: transparent;
  content: '';
}

.search-results__tab:hover,
.search-results__tab:focus-visible,
.search-results__tab.is-active {
  color: var(--app-text-main);
}

.search-results__tab.is-active {
  font-weight: 600;
}

.search-results__tab.is-active::after {
  background: var(--app-text-main);
}

.search-results__section {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
}

.search-results__section-head {
  display: flex;
  min-width: 0;
  align-items: center;
  padding: 0 12px;
}

.search-results__section-title-button {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0;
  border: 0;
  appearance: none;
  background: transparent;
  color: var(--app-text-main);
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  outline: none;
  transition: color var(--app-interaction-duration) var(--app-interaction-ease);
}

.search-results__section-title-button:hover,
.search-results__section-title-button:focus-visible {
  color: var(--app-text-soft);
}

.search-results__entity-card {
  display: inline-flex;
  width: 150px;
  min-width: 0;
  flex: 0 0 150px;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 0;
  border: 0;
  appearance: none;
  background: transparent;
  color: var(--app-text-main);
  cursor: pointer;
  outline: none;
  text-align: center;
  transition: opacity 0.18s ease;
}

.search-results__entity-card:hover,
.search-results__entity-card:focus-visible {
  opacity: 0.86;
}

.search-results__entity-card:active {
  opacity: 0.72;
}

.search-results__entity-avatar {
  display: block;
  width: 150px;
  height: 150px;
  margin-bottom: 4px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--app-avatar-surface);
  box-shadow:
    inset 0 0 0 1px var(--app-avatar-ring),
    var(--app-shadow-soft);
}

.search-results__entity-avatar img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.search-results__entity-title,
.search-results__entity-meta,
.search-results__entity-metrics {
  display: block;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-results__entity-title {
  color: var(--app-text-main);
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
}

.search-results__entity-meta,
.search-results__entity-metrics {
  color: var(--app-text-muted);
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
}

.search-results__person-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 24px 18px;
  padding: 24px 12px 0;
}

.search-results__person-grid .search-results__entity-card {
  justify-self: center;
}

.search-results__cartoon-grid {
  display: grid;
  grid-template-columns: repeat(var(--search-work-columns), minmax(0, 1fr));
  width: 100%;
}

.search-results__topic-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  padding: 8px 12px 0;
}

.search-results__topic-grid.is-preview {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.search-results__topic-card {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 17px;
  padding: 12px;
  border: 0;
  appearance: none;
  border-radius: 16px;
  background: var(--app-surface-hover);
  color: var(--app-text-main);
  cursor: pointer;
  outline: none;
  text-align: left;
  transition: background-color var(--app-interaction-duration) var(--app-interaction-ease);
}

.search-results__topic-card:hover,
.search-results__topic-card:focus-visible {
  background-color: var(--app-state-layer-pressed);
}

.search-results__topic-card:active {
  background-color: var(--app-state-layer-pressed);
}

.search-results__topic-cover {
  display: block;
  width: 80px;
  height: 80px;
  flex: 0 0 auto;
  overflow: hidden;
  border-radius: 8px;
  background: var(--app-avatar-surface-muted);
}

.search-results__topic-cover img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.search-results__topic-copy {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 8px;
}

.search-results__topic-title {
  overflow: hidden;
  color: var(--app-text-main);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-results__topic-metrics {
  overflow: hidden;
  color: var(--app-text-muted);
  font-size: 12px;
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-results__work-masonry {
  display: grid;
  width: 100%;
  grid-template-columns: repeat(var(--search-work-columns), minmax(0, 1fr));
}

.search-results__work-column {
  min-width: 0;
}

.search-results__state {
  margin: 0;
  color: var(--app-text-muted);
  font-size: 14px;
  line-height: 22px;
  text-align: center;
}

.search-results__empty-state {
  display: flex;
  min-width: 0;
  min-height: clamp(280px, calc(100vh - 190px), 620px);
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 16px;
  color: var(--app-text-muted);
  font-size: 14px;
  line-height: 20px;
  text-align: center;
}

.search-results__empty-state p {
  margin: 0;
}

.search-results__empty-image {
  width: var(--app-state-illustration-size);
  height: var(--app-state-illustration-size);
  object-fit: contain;
  opacity: 0.72;
}

.search-results__load-more {
  display: flex;
  min-height: 56px;
  align-items: center;
  justify-content: center;
  color: var(--app-text-muted);
  font-size: 13px;
  line-height: 18px;
}

.search-results__load-more span {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.is-spin {
  animation: search-results-spin 0.9s linear infinite;
}

.search-results__body,
.search-results__loading-shell {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 24px;
}

.search-results__loading-shell {
  pointer-events: none;
}

.search-results__body > *,
.search-results__loading-shell > * {
  animation: search-results-enter 0.32s var(--app-interaction-ease) both;
}

.search-results__body > *:nth-child(2),
.search-results__loading-shell > *:nth-child(2) {
  animation-delay: 0.035s;
}

.search-results__body > *:nth-child(3),
.search-results__loading-shell > *:nth-child(3) {
  animation-delay: 0.07s;
}

.search-results__body > *:nth-child(4),
.search-results__loading-shell > *:nth-child(4) {
  animation-delay: 0.105s;
}

.search-results__skeleton-section {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
}

.search-results__skeleton-heading {
  width: 48px;
  height: 22px;
  margin: 0 12px;
  border-radius: 8px;
}

.search-results__skeleton-strip {
  display: flex;
  min-width: 0;
  gap: 12px;
  overflow: hidden;
  padding: 8px 12px 0;
}

.search-results__entity-skeleton-card {
  display: inline-flex;
  width: 150px;
  min-width: 0;
  flex: 0 0 150px;
  align-items: center;
  flex-direction: column;
  gap: 6px;
}

.search-results__avatar-skeleton,
.search-results__line-skeleton,
.search-results__skeleton-heading,
.search-results__topic-cover-skeleton,
.search-results__topic-line-skeleton,
.search-results__work-cover-skeleton,
.search-results__work-line-skeleton,
.search-results__cartoon-cover-skeleton,
.search-results__cartoon-avatar-skeleton,
.search-results__cartoon-line-skeleton {
  position: relative;
  overflow: hidden;
}

.search-results__avatar-skeleton,
.search-results__topic-cover-skeleton,
.search-results__work-cover-skeleton,
.search-results__cartoon-cover-skeleton,
.search-results__cartoon-avatar-skeleton {
  background: var(--app-skeleton-base);
}

.search-results__line-skeleton,
.search-results__skeleton-heading,
.search-results__topic-line-skeleton,
.search-results__work-line-skeleton,
.search-results__cartoon-line-skeleton {
  background: var(--app-skeleton-line);
}

.search-results__avatar-skeleton::after,
.search-results__line-skeleton::after,
.search-results__skeleton-heading::after,
.search-results__topic-cover-skeleton::after,
.search-results__topic-line-skeleton::after,
.search-results__work-cover-skeleton::after,
.search-results__work-line-skeleton::after,
.search-results__cartoon-cover-skeleton::after,
.search-results__cartoon-avatar-skeleton::after,
.search-results__cartoon-line-skeleton::after {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, var(--app-skeleton-shimmer), transparent);
  content: '';
  transform: translateX(-100%);
  animation: search-results-shimmer 1.35s ease-in-out infinite;
}

.search-results__avatar-skeleton {
  width: 150px;
  height: 150px;
  border-radius: 999px;
}

.search-results__line-skeleton {
  width: 84px;
  height: 14px;
  border-radius: 999px;
}

.search-results__line-skeleton.is-short {
  width: 62px;
}

.search-results__topic-card-skeleton {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 17px;
  padding: 12px;
  border-radius: 16px;
  background: var(--app-surface-hover);
}

.search-results__topic-cover-skeleton {
  width: 80px;
  height: 80px;
  flex: 0 0 auto;
  border-radius: 8px;
}

.search-results__topic-copy-skeleton {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 8px;
}

.search-results__topic-line-skeleton {
  width: 72%;
  height: 14px;
  border-radius: 999px;
}

.search-results__topic-line-skeleton.is-short {
  width: 42%;
}

.search-results__work-card-skeleton {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 12px 16px;
  break-inside: avoid;
}

.search-results__work-cover-skeleton {
  width: 100%;
  aspect-ratio: 293 / 468.8;
  border-radius: 12px;
}

.search-results__work-cover-skeleton.is-short {
  aspect-ratio: 220 / 293.3333;
}

.search-results__work-cover-skeleton.is-square {
  aspect-ratio: 1;
}

.search-results__work-line-skeleton {
  width: 86%;
  height: 14px;
  border-radius: 999px;
}

.search-results__work-line-skeleton.is-short {
  width: 46%;
}

.search-results__cartoon-skeleton {
  padding: 12px;
}

.search-results__cartoon-cover-skeleton {
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: 12px;
}

.search-results__cartoon-cover-skeleton::before {
  position: absolute;
  inset: 30% 0 0;
  z-index: 1;
  background: linear-gradient(180deg, rgb(0 0 0 / 0) 0%, rgb(0 0 0 / 0.18) 100%);
  content: '';
}

.search-results__cartoon-footer-skeleton {
  position: absolute;
  right: 16px;
  bottom: 16px;
  left: 16px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.search-results__cartoon-avatar-row-skeleton {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
}

.search-results__cartoon-avatar-skeleton {
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  border-radius: 999px;
}

.search-results__cartoon-line-skeleton {
  width: 54%;
  height: 14px;
  border-radius: 999px;
}

.search-results__cartoon-line-skeleton.is-wide {
  width: 82%;
}

.search-results__cartoon-line-skeleton.is-short {
  width: 44%;
}

.search-results-body-enter-active,
.search-results-body-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.22s var(--app-interaction-ease);
}

.search-results-body-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.search-results-body-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@keyframes search-results-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes search-results-shimmer {
  to {
    transform: translateX(100%);
  }
}

@keyframes search-results-enter {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 960px) {
  .search-results__topic-grid.is-preview {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .search-results {
    gap: 14px;
  }

  .search-results__body,
  .search-results__loading-shell {
    gap: 14px;
  }

  .search-results__tabs {
    top: 62px;
    justify-content: flex-start;
    padding-inline: 8px;
  }

  .search-results__tab {
    padding: 8px 11px 12px;
    font-size: 15px;
    line-height: 21px;
  }

  .search-results__section-head,
  .search-results__topic-grid {
    padding-inline: 8px;
  }

  .search-results__entity-card {
    width: 96px;
    flex-basis: 96px;
  }

  .search-results__entity-avatar {
    width: 96px;
    height: 96px;
  }

  .search-results__entity-title,
  .search-results__entity-meta,
  .search-results__entity-metrics {
    max-width: 96px;
  }

  .search-results__person-grid {
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 20px 12px;
    padding: 18px 8px 0;
  }

  .search-results__skeleton-heading {
    margin-inline: 8px;
  }

  .search-results__skeleton-strip {
    padding-inline: 8px;
  }

  .search-results__entity-skeleton-card {
    width: 96px;
    flex-basis: 96px;
  }

  .search-results__avatar-skeleton {
    width: 96px;
    height: 96px;
  }

  .search-results__cartoon-grid,
  .search-results__work-masonry {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding-inline: 6px;
  }

  .search-results__topic-grid,
  .search-results__topic-grid.is-preview {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .search-results__topic-card {
    gap: 12px;
    border-radius: 14px;
  }

  .search-results__topic-card-skeleton {
    gap: 12px;
    border-radius: 14px;
  }

  .search-results__topic-cover,
  .search-results__topic-cover-skeleton {
    width: 64px;
    height: 64px;
  }

  .search-results__cartoon-skeleton {
    padding: 7px 5px 14px;
  }

  .search-results__cartoon-cover-skeleton {
    border-radius: 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .search-results__body > *,
  .search-results__loading-shell > *,
  .is-spin,
  .search-results__avatar-skeleton::after,
  .search-results__line-skeleton::after,
  .search-results__skeleton-heading::after,
  .search-results__topic-cover-skeleton::after,
  .search-results__topic-line-skeleton::after,
  .search-results__work-cover-skeleton::after,
  .search-results__work-line-skeleton::after,
  .search-results__cartoon-cover-skeleton::after,
  .search-results__cartoon-avatar-skeleton::after,
  .search-results__cartoon-line-skeleton::after {
    animation: none;
  }

  .search-results-body-enter-active,
  .search-results-body-leave-active {
    transition: none;
  }

  .search-results-body-enter-from,
  .search-results-body-leave-to {
    transform: none;
  }
}
</style>
